

import CookieStore from "./storecookie";


export class GoogleAnalytics
{

    /**
     * The GA client ID for this browser, read from the `_ga` cookie.
     *
     * The cookie is the only source. There used to be a second path that read
     * `ga.getAll()`, and it was removed rather than kept as a fallback: `window.ga`
     * is created by analytics.js (Universal Analytics), GA4's gtag.js never defines
     * it, and UA stopped processing data in July 2023. Even where a site still loads
     * legacy analytics.js, the tracker belongs to a property that collects nothing.
     *
     * Nothing is lost by dropping it: `_ga` is written by UA and GA4 alike, in the
     * same format, so the cookie is a superset of what the tracker could report.
     */
    public static getClientIds() : Array<string>
    {
        const rawValues = new CookieStore().get('_ga');
        const clientIds = [];
        for (let i = 0; i < rawValues.length; i ++) {
            const clientId = GoogleAnalytics.clientIdFromCookieValue(rawValues[i]);
            if (clientId) {
                clientIds.push(clientId);
            }
        }
        return clientIds;
    }

    /**
     * Extract the client ID from one `_ga` cookie value, or '' if it is not a shape
     * we recognise.
     *
     *     GA1.1.1681285157.1730368107   ->   1681285157.1730368107
     *
     * What Google actually documents, and what it does not
     * ---------------------------------------------------
     * Documented: `_ga` is "used to distinguish users" and holds the client ID, while
     * `_ga_<container-id>` is "used to persist session state" - a different cookie
     * with a different meaning, which is why the lookup is exact-match. Also
     * documented: a client ID is a DOT-SEPARATED pair. The GA4 config reference spells
     * it `gtag('set', 'client_id', 'aaa.bbb')`.
     *   https://support.google.com/analytics/answer/11397207
     *   https://developers.google.com/analytics/devguides/collection/ga4/reference/config
     *
     * NOT documented: the cookie's internal layout. `GA1.<domain depth>.<client id>`
     * is reverse-engineered, universally relied upon, and carries no stability
     * guarantee from Google. So this parser is pinned to the two things that ARE
     * documented - the cookie holds the client ID, and a client ID looks like
     * `aaa.bbb` - and not to the layout.
     *
     * How to re-verify (better than any format doc)
     * ---------------------------------------------
     * `gtag('get', '<MEASUREMENT_ID>', 'client_id', cb)` returns the client ID GA
     * itself uses. On any page running GA4, that value must equal what this returns.
     * That is the real contract - the value GA, the Measurement Protocol and BigQuery
     * join on - so check against it, not against a description of the cookie.
     *   https://developers.google.com/tag-platform/gtagjs/reference
     *
     * Deliberately NOT used at runtime: it is async, and it would reintroduce a
     * dependency on a third-party global of exactly the kind removed from this file.
     *
     * Rules
     * -----
     * `<domain depth>` varies with the number of components in the cookie domain
     * (GA1.1, GA1.2, GA1.3...), so the prefix is dropped positionally rather than
     * matched. EXACTLY four segments are required, because the documented client ID is
     * exactly the `aaa.bbb` pair: a shorter value has no pair to take, and a longer one
     * is not a shape we recognise. Taking "everything after the prefix" from a
     * five-segment value would publish `a.b.c` as a client ID - the same
     * wrong-but-present attribution this parser exists to stop, since it looks
     * populated to every metric we have and joins to nothing.
     */
    protected static clientIdFromCookieValue(value: string) : string
    {
        if (typeof value !== 'string') {
            return '';
        }
        // GA1.<depth>.<a>.<b>
        const parts = value.split('.');
        if (parts.length !== 4) {
            return '';
        }
        // reject empty segments ('GA1.1..')
        if (!parts[2] || !parts[3]) {
            return '';
        }
        return parts[2] + '.' + parts[3];
    }

    public static getSessionIds() : Array<string>
    {
        return GoogleAnalytics.generateTimestampIds('s');
    }

    public static getHitIds() : Array<string>
    {
        return GoogleAnalytics.generateTimestampIds('h');
    }

    protected static generateTimestampIds(prefix: string) : Array<string>
    {
        const id = new Date().getTime();
        return [
            `${prefix}${id}`
        ]
    }


}